<template>
  <div class="w-full flex flex-col justify-center items-center">
    <div class="tour-key-area py-4">
      <KeyboardArea
        class="max-w-sm min-[450px]:max-w-md sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl overflow-x-scroll lg:overflow-x-visible"
      />
    </div>

    <div
      class="max-w-5xl flex flex-col md:flex-row justify-center items-center md:items-end pt-4 pb-6 gap-6"
    >
      <ListSwitcher
        class="tour-layer-switcher border border-base-300 rounded-lg shadow py-3 pb-4 px-6 w-full h-full md:min-w-[32rem]"
        title="Layers"
        :entries="kb.availableLayers.map(String)"
        :current="kb.layer"
        @select="(layer) => kb.setLayer(layer)"
        @insert="(layer) => kb.addLayer(layer)"
        @delete="(layer) => kb.removeLayer(layer)"
        :noDelete="kb.layerCount == 1"
        :isHighlighted="(id) => hl.isHighlightedLayer(kb.availableLayers[id])"
      />

      <div class="flex flex-col justify-center items-center gap-3">
        <CompileFirmwareButton class="tour-compile-btn w-64" />
        <div class="tour-firmware-btns join w-64">
          <DownloadFirmwareButton
            @mouseover="onMouseOver('DOWNLOAD')"
            @mouseleave="onMouseLeave('DOWNLOAD')"
            class="transition-all"
            :class="downloadVariant == 'DOWNLOAD' ? 'flex-grow' : ''"
            :small="downloadVariant != 'DOWNLOAD'"
          />
          <FlashFirmwareButton
            @mouseover="onMouseOver('FLASH')"
            @mouseleave="onMouseLeave('FLASH')"
            class="transition-all"
            :class="[
              downloadVariant == 'FLASH' ? 'flex-grow' : '',
              webUsbSupported() ? 'better-border-l' : '',
            ]"
            :small="downloadVariant != 'FLASH'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { JSONSchemaType } from "ajv";
import { ref } from "vue";
import { webUsbSupported } from "@/utils";
import { LocalStorage } from "@/localStorage";
import { useKeyboardStore, useHighlightStore } from "@/stores";
import ListSwitcher from "@/components/utils/ListSwitcher.vue";
import KeyboardArea from "@/components/keys/KeyboardArea.vue";
import CompileFirmwareButton from "@/components/CompileFirmwareButton.vue";
import DownloadFirmwareButton from "@/components/DownloadFirmwareButton.vue";
import FlashFirmwareButton from "@/components/FlashFirmwareButton.vue";

// Switch button sizes after this time of hovering
const BUTTON_SWITCH_DELAY = 100;
const SCHEMA: JSONSchemaType<DownloadVariant> = {
  type: "string",
  enum: ["DOWNLOAD", "FLASH"],
};
const noop = (v: DownloadVariant) => v;
const STORAGE = new LocalStorage("download-variant", SCHEMA, noop, noop);

const initialDownloadVariant = () => {
  if (!webUsbSupported()) {
    STORAGE.set("DOWNLOAD");
    return "DOWNLOAD";
  }
  return STORAGE.get() ?? "FLASH";
};

const kb = useKeyboardStore();
const hl = useHighlightStore();
let timer: number | undefined;

type DownloadVariant = "DOWNLOAD" | "FLASH";

const downloadVariant = ref<DownloadVariant>(initialDownloadVariant());

const onMouseOver = (variant: DownloadVariant) => {
  if (timer == undefined) {
    const onTimeout = () => {
      downloadVariant.value = variant;
      if (webUsbSupported()) STORAGE.set(variant);
    };
    timer = setTimeout(onTimeout, BUTTON_SWITCH_DELAY);
  }
};

const onMouseLeave = (_variant: DownloadVariant) => {
  clearTimeout(timer);
  timer = undefined;
  if (!webUsbSupported()) downloadVariant.value = "DOWNLOAD";
};
</script>

<style>
.better-border-l .btn {
  @apply border-l-accent-focus/50;
}
.better-border-l .btn.btn-disabled {
  @apply border-l-transparent;
}

.better-border-r .btn {
  @apply border-r-accent-focus/50;
}
.better-border-r .btn.btn-disabled {
  @apply border-r-transparent;
}
</style>
